import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useReaderCredits } from '../contexts/ReaderCreditContext';
import { useReaderFunnel } from '../contexts/ReaderFunnelContext';
import ReaderNav from './ReaderNav';
import ReaderTopMenuBar from './ReaderTopMenuBar';
import ReaderMobileBottomNav from './ReaderMobileBottomNav';
import UniversalFooter from './UniversalFooter';
import AboutBookModal from './AboutBookModal';
import { Search, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// BACKEND_URL imported from centralized config

const AuthenticatedCatalogPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { balance } = useReaderCredits();
  const { setShowCreditExpiredModal, isBetaTester } = useReaderFunnel();
  
  const [manuscripts, setManuscripts] = useState([]);
  const [readerLibrary, setReaderLibrary] = useState([]); // Books in reader's library
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [addingToLibrary, setAddingToLibrary] = useState(null);
  
  // Filter states (matching PublicLibraryPage)
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('title-asc');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [jumpToTitle, setJumpToTitle] = useState('');
  const [jumpToAuthor, setJumpToAuthor] = useState('');
  
  // "Continue Reading" modal state
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [continueBook, setContinueBook] = useState(null);

  // Fetch all manuscripts and reader's library
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      // Fetch both catalog and reader's library in parallel
      // Use /api/library/public for catalog to only show activated books
      const [catalogResponse, libraryResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/library/public`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/reader/library`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!catalogResponse.ok) {
        throw new Error('Failed to fetch catalog');
      }

      const catalogData = await catalogResponse.json();
      setManuscripts(catalogData);
      
      if (libraryResponse.ok) {
        const libraryData = await libraryResponse.json();
        // Extract manuscript IDs from reader's library
        const libraryItems = libraryData.library || [];
        setReaderLibrary(libraryItems.map(item => item.manuscriptId));
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Check if a book is in the reader's library
  const isInLibrary = (bookId) => {
    return readerLibrary.includes(bookId);
  };

  // Extract unique genres and themes for dropdowns
  const genres = useMemo(() => {
    const genreSet = new Set(manuscripts.map(m => m.genre).filter(Boolean));
    return ['all', ...Array.from(genreSet).sort()];
  }, [manuscripts]);

  const themes = useMemo(() => {
    const themeSet = new Set(manuscripts.map(m => m.theme).filter(Boolean));
    return ['all', ...Array.from(themeSet).sort()];
  }, [manuscripts]);

  // Extract unique titles and authors for jump dropdowns
  const titles = useMemo(() => {
    return ['', ...manuscripts.map(m => m.title).sort()];
  }, [manuscripts]);

  const authors = useMemo(() => {
    const authorSet = new Set(manuscripts.map(m => m.author).filter(Boolean));
    return ['', ...Array.from(authorSet).sort()];
  }, [manuscripts]);

  // Filter and sort manuscripts
  const filteredManuscripts = useMemo(() => {
    let result = [...manuscripts];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(book =>
        book.title?.toLowerCase().includes(search) ||
        book.author?.toLowerCase().includes(search)
      );
    }

    // Genre filter
    if (selectedGenre !== 'all') {
      result = result.filter(book => book.genre === selectedGenre);
    }

    // Theme filter
    if (selectedTheme !== 'all') {
      result = result.filter(book => book.theme === selectedTheme);
    }

    // Jump to title
    if (jumpToTitle) {
      result = result.filter(book => book.title === jumpToTitle);
    }

    // Jump to author
    if (jumpToAuthor) {
      result = result.filter(book => book.author === jumpToAuthor);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortOrder) {
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '');
        case 'author-asc':
          return (a.author || '').localeCompare(b.author || '');
        case 'author-desc':
          return (b.author || '').localeCompare(a.author || '');
        default:
          return 0;
      }
    });

    return result;
  }, [manuscripts, searchTerm, selectedGenre, selectedTheme, jumpToTitle, jumpToAuthor, sortOrder]);

  // Reset all filters
  const handleShowAllTitles = () => {
    setSearchTerm('');
    setSelectedGenre('all');
    setSelectedTheme('all');
    setJumpToTitle('');
    setJumpToAuthor('');
    setSortOrder('title-asc');
  };

  // Handle book click
  const handleBookClick = (book) => {
    if (isInLibrary(book.id)) {
      // Book is in library - show "Continue Reading" modal
      setContinueBook(book);
      setShowContinueModal(true);
    } else {
      // Book not in library - show details modal
      setSelectedBook(book);
    }
  };

  // Handle "Continue Reading" confirmation
  const handleContinueReading = async () => {
    if (!continueBook) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Open the manuscript session
      const response = await fetch(`${BACKEND_URL}/api/manuscripts/${continueBook.id}/open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to open manuscript');
      }

      const data = await response.json();
      navigate(`/player/${data.session_id}`, {
        state: { returnTo: '/library' }
      });
    } catch (err) {
      console.error('Error opening manuscript:', err);
      alert('Failed to open book. Please try again.');
    } finally {
      setShowContinueModal(false);
      setContinueBook(null);
    }
  };

  // Handle adding book to library
  const handleAddToLibrary = async (book) => {
    try {
      setAddingToLibrary(book.id);
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${BACKEND_URL}/api/reader/library`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ manuscript_id: book.id })
      });

      if (response.ok) {
        // Update local state to reflect the addition
        setReaderLibrary(prev => [...prev, book.id]);
        alert(`"${book.title}" has been added to your library!`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add to library');
      }
    } catch (err) {
      console.error('Error adding to library:', err);
      alert('Failed to add to library. Please try again.');
    } finally {
      setAddingToLibrary(null);
    }
  };

  // Handle reading a book now
  const handleReadNow = async (book) => {
    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[CATALOG] BLOCKED: No credits remaining (balance:', balance, ')');
      toast.error('You need Reader Credits to listen. Please choose a plan to continue.', {
        duration: 5000
      });
      setShowCreditExpiredModal(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // First add to library if not already there
      if (!isInLibrary(book.id)) {
        await fetch(`${BACKEND_URL}/api/reader/library`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ manuscript_id: book.id })
        });
        // Update local state
        setReaderLibrary(prev => [...prev, book.id]);
      }

      // Open the manuscript session
      const response = await fetch(`${BACKEND_URL}/api/manuscripts/${book.id}/open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to open manuscript');
      }

      const data = await response.json();
      navigate(`/player/${data.session_id}`, {
        state: { returnTo: '/library' }
      });
    } catch (err) {
      console.error('Error opening manuscript:', err);
      alert('Failed to open book. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 pb-20 md:pb-0">
      {/* Authenticated Reader Navigation */}
      <ReaderNav />
      <ReaderTopMenuBar />

      {/* Page Header - Consistent blue/purple gradient */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-sky-500">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:py-12 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            NewAudia™ Library
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-sm sm:text-base text-emerald-100/90">
            Browse our complete catalog of NFD-SmartBooks<sup className="text-[0.6em]">™</sup>. Add titles to your personal library or start listening immediately.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-8">
        {/* Filter Controls - Matching PublicLibraryPage */}
        <div className="rounded-2xl bg-slate-900/80 p-6 ring-1 ring-slate-800 mb-8">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div className="relative lg:col-span-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by title or author..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 py-2.5 pl-10 pr-4 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Sort Order */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="title-asc">Title (A-Z)</option>
              <option value="title-desc">Title (Z-A)</option>
              <option value="author-asc">Author (A-Z)</option>
              <option value="author-desc">Author (Z-A)</option>
            </select>

            {/* Jump to Title */}
            <select
              value={jumpToTitle}
              onChange={(e) => setJumpToTitle(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Jump to Title</option>
              {titles.filter(t => t).map(title => (
                <option key={title} value={title}>{title}</option>
              ))}
            </select>

            {/* Jump to Author */}
            <select
              value={jumpToAuthor}
              onChange={(e) => setJumpToAuthor(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Jump to Author</option>
              {authors.filter(a => a).map(author => (
                <option key={author} value={author}>{author}</option>
              ))}
            </select>
          </div>

          {/* Second Row */}
          <div className="grid gap-4 md:grid-cols-2 mt-4">
            {/* Genre Filter */}
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Genres</option>
              {genres.filter(g => g !== 'all').map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>

            {/* Theme Filter */}
            <select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Themes</option>
              {themes.filter(t => t !== 'all').map(theme => (
                <option key={theme} value={theme}>{theme}</option>
              ))}
            </select>
          </div>

          {/* Show All Button */}
          <button
            onClick={handleShowAllTitles}
            className="w-full mt-4 rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm font-semibold text-white hover:bg-slate-700 transition-colors"
          >
            Show All Titles
          </button>
        </div>

        {/* Results Count */}
        {!loading && !error && (
          <p className="text-sm text-slate-400 mb-6">
            Showing {filteredManuscripts.length} of {manuscripts.length} books
          </p>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid gap-6 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[2/3] rounded-lg bg-slate-800 mb-3"></div>
                <div className="h-4 w-3/4 rounded bg-slate-800 mb-1"></div>
                <div className="h-3 w-1/2 rounded bg-slate-800"></div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-2xl bg-red-900/20 p-8 ring-1 ring-red-800 text-center">
            <h3 className="text-lg font-semibold text-red-400">Error Loading Catalog</h3>
            <p className="mt-2 text-sm text-slate-300">{error}</p>
            <button
              onClick={fetchData}
              className="mt-4 rounded-full bg-red-600 px-6 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredManuscripts.length === 0 && (
          <div className="rounded-2xl bg-slate-900/60 p-12 text-center ring-1 ring-slate-800">
            <BookOpen className="mx-auto h-16 w-16 text-slate-600" />
            <h3 className="mt-6 text-xl font-semibold">
              {searchTerm || selectedGenre !== 'all' || selectedTheme !== 'all' 
                ? 'No matching titles found' 
                : 'No titles available yet'}
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              {searchTerm || selectedGenre !== 'all' || selectedTheme !== 'all'
                ? 'Try adjusting your filters or search terms.'
                : 'Check back soon for new audiobook releases.'}
            </p>
            {(searchTerm || selectedGenre !== 'all' || selectedTheme !== 'all') && (
              <button
                onClick={handleShowAllTitles}
                className="mt-4 rounded-full bg-purple-600 px-6 py-2 text-sm font-semibold text-white hover:bg-purple-500 transition-colors"
              >
                Show All Titles
              </button>
            )}
          </div>
        )}

        {/* Book Grid */}
        {!loading && !error && filteredManuscripts.length > 0 && (
          <div className="grid gap-4 grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10">
            {filteredManuscripts.map((book) => {
              const inLibrary = isInLibrary(book.id);
              
              return (
                <div
                  key={book.id}
                  className={`group cursor-pointer max-w-[120px] ${inLibrary ? 'opacity-70' : ''}`}
                  onClick={() => handleBookClick(book)}
                >
                  <div className={`relative aspect-[2/3] w-full overflow-hidden rounded-md bg-slate-800 mb-2 ring-1 transition ${
                    inLibrary 
                      ? 'ring-purple-600/50 grayscale-[30%]' 
                      : 'ring-slate-700 group-hover:ring-purple-500 group-hover:shadow-lg group-hover:shadow-purple-900/20'
                  }`}>
                    {book.cover_url ? (
                      <img
                        src={`${BACKEND_URL}${book.cover_url}?v=${book.cover_updated_at || book.updated_at || Date.now()}`}
                        alt={book.title}
                        className={`h-full w-full object-cover transition ${!inLibrary && 'group-hover:scale-105'}`}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-500">
                        <BookOpen className="h-8 w-8" />
                      </div>
                    )}
                    
                    {/* "In Library" Tag */}
                    {inLibrary && (
                      <div className="absolute top-1 right-1 bg-purple-600 text-white text-[8px] font-semibold px-1.5 py-0.5 rounded-full shadow-lg">
                        In Library
                      </div>
                    )}
                  </div>
                  <h3 className={`font-semibold text-xs line-clamp-2 mb-0.5 transition ${
                    inLibrary 
                      ? 'text-slate-400' 
                      : 'text-slate-50 group-hover:text-purple-400'
                  }`}>
                    {book.title}
                  </h3>
                  <p className="text-[10px] text-slate-400 line-clamp-1">
                    {book.author}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* About Book Modal (for books NOT in library) */}
      <AboutBookModal
        book={selectedBook}
        isOpen={!!selectedBook}
        onClose={() => setSelectedBook(null)}
        isAuthenticated={true}
        onReadNow={handleReadNow}
        onAddToLibrary={handleAddToLibrary}
      />

      {/* Continue Reading Modal (for books IN library) */}
      <AlertDialog open={showContinueModal} onOpenChange={setShowContinueModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            {/* NewAudia Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-12 object-contain"
              />
            </div>
            
            <AlertDialogTitle className="text-2xl font-bold text-center text-purple-600">
              Continue Reading?
            </AlertDialogTitle>
          </AlertDialogHeader>
          
          {/* Body */}
          <AlertDialogDescription className="text-center space-y-4">
            {continueBook && (
              <>
                <p className="text-slate-600">
                  <span className="font-semibold text-slate-800">"{continueBook.title}"</span> is already in your library.
                </p>
                <p className="text-sm text-slate-500">
                  Would you like to continue where you left off?
                </p>
              </>
            )}
          </AlertDialogDescription>
          
          <div className="flex flex-col gap-2 mt-4">
            <button
              onClick={handleContinueReading}
              className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-semibold transition-colors text-lg"
            >
              Continue Reading
            </button>
            <button
              onClick={() => {
                setShowContinueModal(false);
                setContinueBook(null);
              }}
              className="w-full px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <UniversalFooter />
      <ReaderMobileBottomNav />
    </div>
  );
};

export default AuthenticatedCatalogPage;
